package com.speedsoftware.nibbler.ui.components

import androidx.compose.ui.draw.clip
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.speedsoftware.nibbler.ui.AppState
import com.speedsoftware.nibbler.ui.ProjectTab
import com.speedsoftware.nibbler.ui.Screen
import com.speedsoftware.nibbler.ui.theme.NibblerColors

/** One entry of the app's navigation (sidebar on wide screens, bottom bar on phones). */
private class NavEntry(val label: String, val icon: PropertyIcon, val selected: Boolean, val onClick: () -> Unit)

private fun navEntries(appState: AppState): List<NavEntry> {
    val screen = appState.currentScreen
    val inProject = screen is Screen.ProjectDetail
    val tab = appState.selectedProjectTab
    val entries = mutableListOf(
        NavEntry("Projects", PropertyIcon.FOLDER, screen is Screen.ProjectsList) { appState.navigateTo(Screen.ProjectsList) }
    )
    if (inProject) {
        entries += NavEntry("Board", PropertyIcon.BOARD, tab == ProjectTab.BOARDS) { appState.selectedProjectTab = ProjectTab.BOARDS }
        entries += NavEntry("Docs", PropertyIcon.DOC, tab == ProjectTab.DOCS) { appState.selectedProjectTab = ProjectTab.DOCS }
        entries += NavEntry("Team", PropertyIcon.PEOPLE, tab == ProjectTab.MEMBERS) { appState.selectedProjectTab = ProjectTab.MEMBERS }
        entries += NavEntry("Project settings", PropertyIcon.SLIDERS, tab == ProjectTab.SETTINGS) { appState.selectedProjectTab = ProjectTab.SETTINGS }
    }
    if (appState.isSuperAdmin) {
        entries += NavEntry("Admin", PropertyIcon.SHIELD, screen is Screen.AdminDashboard) { appState.navigateTo(Screen.AdminDashboard) }
    }
    return entries
}

/**
 * The frame around every screen: sidebar + top bar on wide screens, top bar + bottom navigation on phones.
 * [title] and [subtitle] fill the top bar ("Board / Sprint 24"); [actions] sit on its right.
 */
@Composable
fun ResponsiveScaffold(
    appState: AppState,
    title: String,
    actions: @Composable () -> Unit = {},
    onNewIssueClick: (() -> Unit)? = null,
    subtitle: String? = null,
    content: @Composable () -> Unit
) {
    BoxWithConstraints(modifier = Modifier.fillMaxSize().background(NibblerColors.Background)) {
        if (maxWidth >= 768.dp) {
            Row(modifier = Modifier.fillMaxSize()) {
                if (appState.isAuthenticated) {
                    AppSidebar(
                        appState = appState,
                        onNewIssueClick = onNewIssueClick,
                        modifier = Modifier
                            .width(264.dp)
                            .fillMaxHeight()
                            .background(NibblerColors.SurfaceContainerLowest)
                    )
                    Box(modifier = Modifier.width(1.dp).fillMaxHeight().background(NibblerColors.OutlineVariant))
                }
                Column(modifier = Modifier.weight(1f).fillMaxHeight().background(NibblerColors.Background)) {
                    TopBar(title, subtitle, actions, compact = false)
                    Box(modifier = Modifier.fillMaxSize()) { content() }
                }
            }
        } else {
            Scaffold(
                topBar = {
                    Box(modifier = Modifier.windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Top + WindowInsetsSides.Horizontal))) {
                        TopBar(title, subtitle, actions, compact = true)
                    }
                },
                bottomBar = {
                    if (appState.isAuthenticated) {
                        Box(
                            modifier = Modifier
                                .background(NibblerColors.SurfaceContainerLowest)
                                .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Bottom + WindowInsetsSides.Horizontal))
                        ) { MobileNavBar(appState) }
                    }
                },
                containerColor = NibblerColors.Background
            ) { paddingValues ->
                Box(modifier = Modifier.fillMaxSize().padding(paddingValues).background(NibblerColors.Background)) { content() }
            }
        }
    }
}

@Composable
private fun TopBar(title: String, subtitle: String?, actions: @Composable () -> Unit, compact: Boolean) {
    Column {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(if (compact) 56.dp else 64.dp)
                .padding(horizontal = if (compact) 16.dp else 28.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Row(modifier = Modifier.weight(1f), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                Text(
                    text = title,
                    fontSize = if (compact) 18.sp else 22.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = NibblerColors.OnSurface,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f, fill = false)
                )
                if (subtitle != null && !compact) {
                    Text("/", fontSize = 18.sp, color = NibblerColors.OnSurfaceVariant)
                    Text(subtitle, fontSize = 15.sp, color = NibblerColors.OnSurfaceVariant, maxLines = 1, overflow = TextOverflow.Ellipsis)
                }
            }
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) { actions() }
        }
        Box(modifier = Modifier.fillMaxWidth().height(1.dp).background(NibblerColors.OutlineVariant))
    }
}

@Composable
fun AppSidebar(
    appState: AppState,
    onNewIssueClick: (() -> Unit)? = null,
    modifier: Modifier = Modifier
) {
    Column(modifier = modifier.padding(horizontal = 14.dp, vertical = 16.dp)) {
        // Workspace header
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 6.dp, vertical = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Box(
                modifier = Modifier.size(38.dp).background(NibblerColors.AccentSoft, RoundedCornerShape(10.dp)),
                contentAlignment = Alignment.Center
            ) {
                Text("N", fontSize = 18.sp, fontWeight = FontWeight.Bold, color = NibblerColors.Accent)
            }
            Column {
                Text("Nibbler", fontSize = 17.sp, fontWeight = FontWeight.SemiBold, color = NibblerColors.OnSurface)
                Text("Issue tracker", fontSize = 12.sp, color = NibblerColors.OnSurfaceVariant)
            }
        }

        Spacer(modifier = Modifier.height(18.dp))

        // Primary action
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(44.dp)
                .background(NibblerColors.Accent, RoundedCornerShape(10.dp))
                .clickable {
                    if (onNewIssueClick != null) onNewIssueClick()
                    else if (appState.currentScreen !is Screen.ProjectsList) appState.selectedProjectTab = ProjectTab.BOARDS
                },
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center
        ) {
            PropertyIconView(PropertyIcon.PLUS, size = 16.dp, color = Color.White)
            Spacer(modifier = Modifier.width(8.dp))
            Text("Create issue", fontSize = 14.sp, fontWeight = FontWeight.SemiBold, color = Color.White)
        }

        Spacer(modifier = Modifier.height(18.dp))

        // Navigation
        Column(modifier = Modifier.weight(1f).verticalScroll(rememberScrollState()), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            navEntries(appState).forEach { SidebarItem(it) }
        }

        // Footer: settings + signed-in user
        Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
            SidebarItem(
                NavEntry("Settings", PropertyIcon.SETTINGS, appState.currentScreen is Screen.Settings) { appState.navigateTo(Screen.Settings()) }
            )
            Spacer(modifier = Modifier.height(8.dp))
            Box(modifier = Modifier.fillMaxWidth().height(1.dp).background(NibblerColors.OutlineVariant))
            Spacer(modifier = Modifier.height(8.dp))
            UserRow(appState)
        }
    }
}

@Composable
private fun SidebarItem(entry: NavEntry) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .clickable { entry.onClick() }
            .background(if (entry.selected) NibblerColors.AccentSoft else Color.Transparent, RoundedCornerShape(10.dp))
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        PropertyIconView(entry.icon, size = 18.dp, color = if (entry.selected) NibblerColors.Accent else NibblerColors.OnSurfaceVariant)
        Text(
            entry.label,
            fontSize = 14.sp,
            fontWeight = if (entry.selected) FontWeight.SemiBold else FontWeight.Medium,
            color = if (entry.selected) NibblerColors.Accent else NibblerColors.OnSurface,
            modifier = Modifier.weight(1f)
        )
        if (entry.selected) Box(modifier = Modifier.size(6.dp).background(NibblerColors.Accent, RoundedCornerShape(3.dp)))
    }
}

@Composable
private fun UserRow(appState: AppState) {
    val user = appState.currentUser ?: return
    var menu by remember { mutableStateOf(false) }
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 4.dp, vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Row(
            modifier = Modifier.weight(1f).clickable { appState.navigateTo(Screen.Settings()) },
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            UserAvatar(user, size = 34.dp)
            Column(modifier = Modifier.weight(1f)) {
                Text(user.name, fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = NibblerColors.OnSurface, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text(user.email, fontSize = 11.sp, color = NibblerColors.OnSurfaceVariant, maxLines = 1, overflow = TextOverflow.Ellipsis)
            }
        }
        Box {
            Box(modifier = Modifier.size(30.dp).clickable { menu = true }, contentAlignment = Alignment.Center) {
                PropertyIconView(PropertyIcon.DOTS, size = 16.dp)
            }
            DropdownMenu(expanded = menu, onDismissRequest = { menu = false }, modifier = Modifier.background(NibblerColors.SurfaceContainerHigh)) {
                DropdownMenuItem(
                    text = { Text("Settings", color = NibblerColors.OnSurface, fontSize = 13.sp) },
                    leadingIcon = { PropertyIconView(PropertyIcon.SETTINGS, size = 16.dp) },
                    onClick = { menu = false; appState.navigateTo(Screen.Settings()) }
                )
                DropdownMenuItem(
                    text = { Text("Log out", color = NibblerColors.Danger, fontSize = 13.sp) },
                    leadingIcon = { PropertyIconView(PropertyIcon.LOGOUT, size = 16.dp, color = NibblerColors.Danger) },
                    onClick = { menu = false; appState.logout() }
                )
            }
        }
    }
}

@Composable
private fun MobileNavBar(appState: AppState) {
    val entries = navEntries(appState).filter { it.label != "Team" } +
        NavEntry("Settings", PropertyIcon.SETTINGS, appState.currentScreen is Screen.Settings) { appState.navigateTo(Screen.Settings()) }
    Column {
        Box(modifier = Modifier.fillMaxWidth().height(1.dp).background(NibblerColors.OutlineVariant))
        Row(modifier = Modifier.fillMaxWidth().height(60.dp), verticalAlignment = Alignment.CenterVertically) {
            entries.forEach { entry ->
                Column(
                    modifier = Modifier.weight(1f).fillMaxHeight().clickable { entry.onClick() },
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    PropertyIconView(entry.icon, size = 20.dp, color = if (entry.selected) NibblerColors.Accent else NibblerColors.OnSurfaceVariant)
                    Spacer(modifier = Modifier.height(3.dp))
                    Text(
                        if (entry.label == "Project settings") "Project" else entry.label,
                        fontSize = 10.sp,
                        fontWeight = if (entry.selected) FontWeight.SemiBold else FontWeight.Normal,
                        color = if (entry.selected) NibblerColors.Accent else NibblerColors.OnSurfaceVariant,
                        maxLines = 1
                    )
                }
            }
        }
    }
}

@androidx.compose.ui.tooling.preview.Preview
@Composable
private fun AppSidebarPreview() {
    val state = AppState()
    AppSidebar(appState = state, modifier = Modifier.width(264.dp).fillMaxHeight().background(NibblerColors.SurfaceContainerLowest))
}
